import { useFormInput } from '@/components/hooks/useFormInput'
import { AccountSectionWrapper, Separator } from './AccountSectionWrapper'
import { FieldText } from './FieldText'
import { FieldSwitch } from './FieldSwitch'
import { AccountBanner } from './AccountBanner'
import { AccountYourFitClub } from './AccountYourFitClub'
import { useAccountLayoutContext } from './AccountProfileContext'
import { useCallback, useEffect, useMemo, useState } from 'preact/hooks'
import { Profile } from '@omneo/omneo-sdk'
import { FieldDate } from './FieldDate'
import { birthdayStringToOmneoObject } from '../helpers/parseBirthday'
import { Form, Page } from './AccountPage'
import { Suspense } from './Suspense'
import { useToggle } from '../hooks/useToggle'
import { formatMobile } from '@/components/helpers/utils'
import { cleanStoreData } from '../helpers/stores'
import { FieldSelect } from './FieldSelect'
import { accountTranslations } from '../helpers/translations'

/**
 *
 * @param {{title: string, body: string, children: Readonly<React.ReactNode>}} props
 * @returns
 */
const ProfileMarketWrapper = (props) => {
  return (
    <div className={'flex flex-col gap-6'}>
      <div className={'flex flex-col gap-2'}>
        <p className={'paragraph-lg font-bold'}>{props.title}</p>
        <p>{props.body}</p>
      </div>
      <div className={'flex flex-col justify-between gap-4 md:flex-row'}>{props.children}</div>
    </div>
  )
}

/**
 *
 * @param {{props: {profile: Profile}}} props
 */
const ProfileDetailsSection = ({ props }) => {
  const { omneoClient, cacheConfig } = useAccountLayoutContext()
  const { isActive, handleToggle } = useToggle()
  const [error, setError] = useState(null)
  const [formChanged, setFormChanged] = useState(false)
  const [initialValues, setInitialValues] = useState(null)

  const { formInputs, handleOnChange } = useFormInput({
    firstName: props?.profile?.first_name ?? '',
    lastName: props?.profile?.last_name ?? '',
    email: props?.profile?.email ?? '',
    mobile: props?.profile?.mobile_phone ?? '',
    birthday: props?.profile?.birth_date ?? '',
    preferred_location_id: props?.profile?.preferred_location_id ?? ''
  })

  // Store initial values when component first loads
  useMemo(() => {
    if (!initialValues && props?.profile) {
      setInitialValues({
        first_name: props.profile.first_name || '',
        last_name: props.profile.last_name || '',
        email: props.profile.email || '',
        mobile_phone: props.profile.mobile_phone || '',
        birth_date: props.profile.birth_date || '',
        preferred_location_id: props.profile.preferred_location_id || ''
      })
    }
  }, [props.profile])

  // Check for changes whenever form inputs change
  useMemo(() => {
    if (initialValues) {
      const currentValues = {
        first_name: formInputs.firstName.value,
        last_name: formInputs.lastName.value,
        email: formInputs.email.value,
        mobile_phone: formInputs.mobile.value,
        birth_date: formInputs.birthday.value,
        preferred_location_id: formInputs.preferred_location_id.value
      }

      // Compare current values with initial values
      const hasChanges = Object.keys(currentValues).some((key) => currentValues[key] !== initialValues[key])

      setFormChanged(hasChanges)
    }
  }, [formInputs, initialValues])

  // Custom onChange handler to track changes
  const handleInputChange = (e) => {
    handleOnChange(e)
  }

  return (
    <AccountSectionWrapper showDivider={false} title={accountTranslations.my_profile.details_form_title}>
      {error && <h4 className={'h4 text-red'}>{error}</h4>}
      <Form
        isActive={isActive}
        isDisabled={!formChanged}
        onSubmit={async (e) => {
          e.preventDefault()
          if (!formChanged) return

          setError(null)
          handleToggle()

          const mobile = formatMobile(formInputs.mobile.value)
          const birthday = formInputs.birthday.value ? birthdayStringToOmneoObject(formInputs.birthday.value) : null

          const res = await omneoClient.profileClient.update({
            first_name: formInputs.firstName.value,
            last_name: formInputs.lastName.value,
            mobile_phone: mobile,
            ...(birthday || {
              birth_day: null,
              birth_month: null,
              birth_year: null
            }),
            ...(formInputs.preferred_location_id.value && {
              preferred_location_id: Number(formInputs.preferred_location_id.value)
            })
          })
          if (!res?.data) {
            setError(res.message)
            handleToggle()
            return
          }

          cacheConfig.updateCacheConfig('Profiles', res.data)

          // Update initial values and reset form changed state
          setInitialValues({
            first_name: formInputs.firstName.value,
            last_name: formInputs.lastName.value,
            email: formInputs.email.value,
            mobile_phone: formInputs.mobile.value,
            birth_date: formInputs.birthday.value,
            ...(formInputs.preferred_location_id.value && {
              preferred_location_id: Number(formInputs.preferred_location_id.value)
            })
          })

          setFormChanged(false)
          handleToggle()
        }}
      >
        <div class={'flex flex-row gap-4'}>
          <FieldText
            type="text"
            autoComplete="given-name"
            label={accountTranslations.form_labels.first_name}
            required={true}
            name={formInputs.firstName.key}
            value={formInputs.firstName.value}
            onChange={handleInputChange}
          />
          <FieldText
            type="text"
            autoComplete="family-name"
            label={accountTranslations.form_labels.last_name}
            required={true}
            name={formInputs.lastName.key}
            value={formInputs.lastName.value}
            onChange={handleInputChange}
          />
        </div>
        <div class={'flex flex-row gap-4'}>
          <FieldText
            type="email"
            autoComplete="email"
            label={accountTranslations.form_labels.email}
            required={true}
            readOnly={true}
            name={formInputs.email.key}
            value={formInputs.email.value}
            onChange={handleInputChange}
          />
          <FieldText
            type="tel"
            autoComplete="tel"
            label={accountTranslations.form_labels.mobile}
            name={formInputs.mobile.key}
            value={formInputs.mobile.value}
            onChange={handleInputChange}
            placeholder={accountTranslations.form_placeholders.mobile}
          />
        </div>
        <div class={'flex flex-row gap-4'}>
          <FieldDate
            label={accountTranslations.form_labels.birthday}
            name={formInputs.birthday.key}
            value={formInputs.birthday.value}
            onChange={handleInputChange}
          />
          <FieldSelect
            name={formInputs.preferred_location_id.key}
            value={formInputs.preferred_location_id.value}
            label={accountTranslations.form_labels.favourite_store}
            options={props.stores.map((s) => ({ value: s.id, label: s.name }))}
            onChange={handleInputChange}
          />
        </div>
      </Form>
    </AccountSectionWrapper>
  )
}

/**
 *
 * @param {{profile: Profile}} param0
 * @returns
 */
const AccountMarketPreferences = ({ profile }) => {
  const { omneoClient } = useAccountLayoutContext()
  const attributes = useMemo(() => profile?.attributes, [])
  const [formChanged, setFormChanged] = useState(false)
  const [initialValues, setInitialValues] = useState(null)
  const { isActive, handleToggle } = useToggle()

  const { formInputs, handleOnChange } = useFormInput(
    {
      newsOffersEmail: attributes?.comms.email_promo,
      newsOffersSms: attributes?.comms.sms_promo,
      vipCommunicationEmail: attributes?.comms.email_benefits,
      vipCommunicationSms: attributes?.comms.sms_benefits,
      alertsEmail: attributes?.comms.email_service,
      alertsSms: attributes?.comms.sms_service,
      optOut: attributes?.comms.email_optout && attributes?.comms.sms_optout
    },
    {
      newsOffersEmail: 'boolean',
      newsOffersSms: 'boolean',
      vipCommunicationEmail: 'boolean',
      vipCommunicationSms: 'boolean',
      alertsEmail: 'boolean',
      alertsSms: 'boolean',
      optOut: 'boolean'
    }
  )

  // When all the form inputs are false, set the optOut to true
  useEffect(() => {
    if (
      formInputs.newsOffersEmail.value === false &&
      formInputs.newsOffersSms.value === false &&
      formInputs.vipCommunicationEmail.value === false &&
      formInputs.vipCommunicationSms.value === false &&
      formInputs.alertsEmail.value === false &&
      formInputs.alertsSms.value === false
    ) {
      handleOnChange({
        currentTarget: {
          name: formInputs.optOut.key,
          value: true
        }
      })
    }
  }, [
    formInputs.newsOffersEmail.value,
    formInputs.newsOffersSms.value,
    formInputs.vipCommunicationEmail.value,
    formInputs.vipCommunicationSms.value,
    formInputs.alertsEmail.value,
    formInputs.alertsSms.value
  ])

  // When any of the form inputs become true and optOut is true, update the optOut to false
  useEffect(() => {
    if (
      formInputs.optOut.value &&
      (formInputs.newsOffersEmail.value ||
        formInputs.newsOffersSms.value ||
        formInputs.vipCommunicationEmail.value ||
        formInputs.vipCommunicationSms.value ||
        formInputs.alertsEmail.value ||
        formInputs.alertsSms.value)
    ) {
      handleOnChange({
        currentTarget: {
          name: formInputs.optOut.key,
          value: false
        }
      })
    }
  }, [
    formInputs.optOut.value,
    formInputs.newsOffersEmail.value,
    formInputs.newsOffersSms.value,
    formInputs.vipCommunicationEmail.value,
    formInputs.vipCommunicationSms.value,
    formInputs.alertsEmail.value,
    formInputs.alertsSms.value
  ])

  // Store initial values when component first loads
  useMemo(() => {
    if (!initialValues && attributes?.comms) {
      setInitialValues({
        email_promo: attributes.comms.email_promo,
        email_benefits: attributes.comms.email_benefits,
        email_reminders: attributes.comms.email_reminders,
        email_service: attributes.comms.email_service,
        email_optout: attributes.comms.email_optout,
        sms_promo: attributes.comms.sms_promo,
        sms_benefits: attributes.comms.sms_benefits,
        sms_reminders: attributes.comms.sms_reminders,
        sms_service: attributes.comms.sms_service,
        sms_optout: attributes.comms.sms_optout
      })
    }
  }, [attributes])

  // Check for changes whenever form inputs change
  useMemo(() => {
    if (initialValues) {
      const currentValues = {
        email_promo: formInputs.newsOffersEmail.value,
        email_benefits: formInputs.vipCommunicationEmail.value,
        email_reminders: formInputs.vipCommunicationEmail.value,
        email_service: formInputs.alertsEmail.value,
        email_optout: formInputs.optOut.value,
        sms_promo: formInputs.newsOffersSms.value,
        sms_benefits: formInputs.vipCommunicationSms.value,
        sms_reminders: formInputs.vipCommunicationSms.value,
        sms_service: formInputs.alertsSms.value,
        sms_optout: formInputs.optOut.value
      }

      // Compare current values with initial values
      const hasChanges = Object.keys(currentValues).some((key) => currentValues[key] !== initialValues[key])

      setFormChanged(hasChanges)
    }
  }, [formInputs, initialValues])

  const handleUpdateAll = (e) => {
    handleOnChange(e)
    if (formInputs.optOut) {
      const keys = [
        'newsOffersEmail',
        'newsOffersSms',
        'vipCommunicationEmail',
        'vipCommunicationSms',
        'alertsEmail',
        'alertsSms'
      ]

      if (e.currentTarget.value === 'true') {
        for (const key of keys) {
          handleOnChange({
            currentTarget: {
              name: key,
              value: false
            }
          })
        }
      }
    }
  }

  return (
    <AccountSectionWrapper showDivider={false} title={accountTranslations.my_profile.marketing_preferences_form.title}>
      <Form
        isActive={isActive}
        isDisabled={!formChanged}
        onSubmit={async (e) => {
          e.preventDefault()
          if (!formChanged) return

          handleToggle()
          const data = {
            email_promo: formInputs.newsOffersEmail.value,
            email_benefits: formInputs.vipCommunicationEmail.value,
            email_reminders: formInputs.vipCommunicationEmail.value,
            email_service: formInputs.alertsEmail.value,
            email_optout: formInputs.optOut.value,
            sms_promo: formInputs.newsOffersSms.value,
            sms_benefits: formInputs.vipCommunicationSms.value,
            sms_reminders: formInputs.vipCommunicationSms.value,
            sms_service: formInputs.alertsSms.value,
            sms_optout: formInputs.optOut.value
          }
          const response = await omneoClient.profileClient.updateCommunicationPreferences(data)

          // Reset form changed state and update initial values after successful save
          if (response) {
            setInitialValues(data)
            setFormChanged(false)
          }

          handleToggle()
        }}
      >
        <ProfileMarketWrapper
          title={accountTranslations.my_profile.marketing_preferences_form.news_and_offers.title}
          body={accountTranslations.my_profile.marketing_preferences_form.news_and_offers.description}
        >
          <FieldSwitch
            onChange={handleOnChange}
            name={formInputs.newsOffersEmail.key}
            value={formInputs.newsOffersEmail.value}
            options={[
              {
                label: 'Notify via email'
              }
            ]}
            label={''}
          />
          <FieldSwitch
            onChange={handleOnChange}
            name={formInputs.newsOffersSms.key}
            value={formInputs.newsOffersSms.value}
            options={[
              {
                label: 'Notify via SMS'
              }
            ]}
            label={''}
          />
        </ProfileMarketWrapper>
        <ProfileMarketWrapper
          title={accountTranslations.my_profile.marketing_preferences_form.vip_communications.title}
          body={accountTranslations.my_profile.marketing_preferences_form.vip_communications.description}
        >
          <FieldSwitch
            onChange={handleOnChange}
            name={formInputs.vipCommunicationEmail.key}
            value={formInputs.vipCommunicationEmail.value}
            options={[
              {
                label: 'Notify via email'
              }
            ]}
            label={''}
          />
          <FieldSwitch
            onChange={handleOnChange}
            name={formInputs.vipCommunicationSms.key}
            value={formInputs.vipCommunicationSms.value}
            options={[
              {
                label: 'Notify via SMS'
              }
            ]}
            label={''}
          />
        </ProfileMarketWrapper>
        <ProfileMarketWrapper
          title={accountTranslations.my_profile.marketing_preferences_form.alerts.title}
          body={accountTranslations.my_profile.marketing_preferences_form.alerts.description}
        >
          <FieldSwitch
            onChange={handleOnChange}
            name={formInputs.alertsEmail.key}
            value={formInputs.alertsEmail.value}
            options={[
              {
                label: 'Notify via email'
              }
            ]}
            label={''}
          />
          <FieldSwitch
            onChange={handleOnChange}
            name={formInputs.alertsSms.key}
            value={formInputs.alertsSms.value}
            options={[
              {
                label: 'Notify via SMS'
              }
            ]}
            label={''}
          />
        </ProfileMarketWrapper>
        <div className={'pt-4'}>
          <FieldSwitch
            onChange={handleUpdateAll}
            name={formInputs.optOut.key}
            value={formInputs.optOut.value}
            options={[
              {
                label: accountTranslations.my_profile.marketing_preferences_form.opt_out_non_essential
              }
            ]}
            label={''}
          />
        </div>
      </Form>
    </AccountSectionWrapper>
  )
}

export const AccountProfile = () => {
  const { handPushChildState, cacheConfig, omneoClient } = useAccountLayoutContext()
  const [refetch, setRefetch] = useState(0)
  const handleRefetch = () => {
    setRefetch((n) => (n += 1))
  }
  const [pageData, setPageData] = useState({
    profile: cacheConfig?.cacheData?.Profiles ?? null,
    stores: [],
    card: {
      id: null,
      memberSince: null,
      balance: 0
    }
  })

  const profilePageDataFetch = useCallback(async () => {
    const [profilePayload, stores] = await Promise.all([
      omneoClient.profileClient.get(),
      omneoClient.profileClient.getStores()
    ])

    cacheConfig.updateCacheConfig('Profiles', profilePayload)

    setPageData(() => ({
      profile: profilePayload,
      stores: cleanStoreData(stores)
    }))
  }, [])

  return (
    <Page
      props={{
        refetch,
        page: {
          title: accountTranslations.my_profile.title
        },
        suspense: {
          asyncCallback: profilePageDataFetch,
          skipFetch: !!pageData.profile?.id,
          loadingState: (
            <div className="space-y-8 md:space-y-10">
              <div className="inline-block w-fit animate-pulse bg-primary-grey">
                <div className="font-bold h3 text-primary-grey">{accountTranslations.my_profile.details_form_title}</div>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="h-[84px] animate-pulse bg-primary-grey" />
                  <div className="h-[84px] animate-pulse bg-primary-grey" />
                  <div className="h-[84px] animate-pulse bg-primary-grey" />
                  <div className="h-[84px] animate-pulse bg-primary-grey" />
                </div>
                <div className="h-[84px] animate-pulse bg-primary-grey" />
                <div className="h-[44px] w-[100px] animate-pulse bg-primary-grey" />
              </div>
            </div>
          )
        }
      }}
    >
      <ProfileDetailsSection props={{ profile: pageData.profile, stores: pageData.stores, handleRefetch }} />
      <Separator />
      <AccountMarketPreferences profile={pageData.profile} />
      <Separator />
      <AccountBanner
        className="-rotate-1 bg-secondary-violet/30"
        title={accountTranslations.addresses.looking_for_addresses}
        cta={{
          title: accountTranslations.addresses.add_address,
          callback: () => {
            handPushChildState('addresses', 'add_address')
          }
        }}
      />
      <Suspense
        props={{
          asyncCallback: async () => {
            const profile = cacheConfig.cacheData.Profiles
            const balance = cacheConfig.cacheData.Balance
            const memberSince = new Date(profile?.created_at)
            const code = profile?.identities?.find((identity) => identity?.handle === 'ap21_card')
            setPageData((p) => ({
              ...p,
              card: {
                id: code?.identifier,
                memberSince: memberSince.getFullYear(),
                balance: balance.reward_balance
              }
            }))
          }
        }}
        loadingState={<></>}
      >
        <AccountYourFitClub card={pageData.card} />
      </Suspense>
    </Page>
  )
}
